import React from "react";
import {Typography} from "@mui/material";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import * as util from "../utils/util";
import {getStressLevel, getStressPercent, STRESS_LEVELS} from "../utils/deviceInfo";

/**
 * ECG 스트레스 지수(SI) — 5단계 색상 스펙트럼 가로 게이지.
 *
 * 지수는 AP_009_004 의 `stress_index` 다. 단계 기준은 바론 UI 기준을 따른다
 * (deviceInfo 의 STRESS_LEVELS).
 *
 * 막대는 **다섯 칸을 균등 분할**한다. 단계별 실제 지수 폭이 50/100/350/400/600 으로
 * 크게 달라 그 비율대로 그리면 1단계 칸이 3% 밖에 안 되어 읽을 수 없다. 대신 바늘 위치를
 * getStressPercent 로 계산해, 균등 칸에서도 항상 자기 단계 칸 안에 들어오게 한다.
 *
 * index 가 null 이면 미측정이다. 그날 기록이 없거나 측정이 전부 부정맥으로 잡혀
 * 지수가 내려오지 않은 경우로, 영역을 숨기지 않고 빈 값으로 그린다.
 */
const StressGaugeBar = (props: { index: number | null }) => {
  const measured = props.index !== null && props.index !== undefined;
  const score = measured ? Number(props.index) : 0;
  const level = measured ? getStressLevel(score) : undefined;
  const percent = getStressPercent(score);

  return (
    <Box sx={{ p: '4px 10px 10px 10px' }}>
      {/* 지수 / 단계 */}
      <Stack direction={'row'} alignItems={'baseline'} justifyContent={'center'} sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 30, fontWeight: 700, color: level ? level.color : '#aaa' }}>
          {measured ? util.formatComma(Math.round(score)) : '-'}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#777', ml: .5, mr: 1.5 }}>점</Typography>
        {level === undefined ? (
          <Typography sx={{ fontSize: 14, color: '#888' }}>미측정</Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: level.color }}>{level.label}</Typography>
            <Typography sx={{ fontSize: 12, color: '#888', ml: .5 }}>({level.level}단계)</Typography>
          </>
        )}
      </Stack>

      <Box sx={{ position: 'relative', pt: 1.5 }}>
        {/* 현재 지수 위치 — 측정값이 있을 때만 */}
        {level === undefined ? null : (
          <Box sx={{ position: 'absolute', top: 0, left: percent + '%', transform: 'translateX(-50%)' }}>
            <Box sx={{
              width: 0, height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid ' + level.color
            }} />
          </Box>
        )}

        {/* 다섯 칸 균등 분할 */}
        <Stack direction={'row'} sx={{ height: 14, borderRadius: '7px', overflow: 'hidden', opacity: measured ? 1 : 0.35 }}>
          {STRESS_LEVELS.map((stressLevel) => (
            <Box key={stressLevel.level} sx={{ flex: 1, bgcolor: stressLevel.color }} />
          ))}
        </Stack>

        <Stack direction={'row'} sx={{ mt: .5 }}>
          {STRESS_LEVELS.map((stressLevel) => (
            <Typography key={stressLevel.level} sx={{
              flex: 1, textAlign: 'center', fontSize: 11,
              color: level && stressLevel.level === level.level ? stressLevel.color : '#aaa',
              fontWeight: level && stressLevel.level === level.level ? 700 : 400
            }}>
              {stressLevel.label}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

export default StressGaugeBar;
